import axios from "axios";
import { API_BASE_URL } from "./apiBase";
import { clearCachedUserInfo } from "./userInfoCache";
import {
  announceBlockedLicense,
  rememberBlockedLicense,
} from "./licenseBlockedHandoff";
import {
  isLicenseBlockedDetail,
  type LicenseBlockedDetail,
} from "./licenseNotice";
import {
  CTX_KEY,
  PING_KEY,
  PONG_KEY,
  clearEmployeeRecordIf,
  clearToken,
  getAccountIsPersonal,
  getTerminalToken,
  readEmployeeRecord,
  ssGet,
  ssSet,
} from "./auth";

/**
 * ЕДИНСТВЕНОТО място в приложението, което решава дали провалена автентикация
 * връща терминала само до ПИН екрана, или го изхвърля напълно на /login.
 *
 * ПРАВИЛОТО Е ЕДНО: жив ли е X — токенът на самия терминал?
 *  • X жив     → само до ПИН. Трите ключа на терминала (access_token,
 *                account_is_personal, shared_account_name) НЕ се пипат — те са
 *                ОБЩИ за всички табове/прозорци и триенето им сваля и валидния
 *                прозорец на съседа (точно бъгът, който поправяме).
 *  • X мъртъв  → пълен изход. ПИН екран без жив X е задънена улица: нов ред в
 *                user_sessions се създава САМО от вход с потребител+парола, а
 *                login-with-code без активна сесия връща грешка — тоест никакъв
 *                код няма да проработи.
 *  • Не знаем  → до ПИН. Пълен изход САМО при ДОКАЗАНО мъртъв X: иначе един
 *                рестарт на бекенда би поискал парола от всички каси наведнъж.
 *  • Спрян лиценз (403 `license_expired`) → пълен изход без проба
 *                (`endSessionForExpiredLicense`) — освен когато на общ терминал
 *                е отказан СЛУЖИТЕЛЯТ: терминал със „Справки"/„Склад" е в режим
 *                „само преглед" и жив, затова същото правило като горе (проба).
 *
 * ИНВАРИАНТ: върне ли някой `clearToken()` в 401 пътя или в WebSocket-а, цялото
 * зацикляне се връща наведнъж. Изборът трябва да остане в тази една функция.
 */

// Единствен полет: десетки заявки могат да гръмнат с 401 наведнъж (react-query
// refetch при фокус на прозореца) — обработваме първата, останалите я чакат.
let pending: Promise<void> | null = null;
// Тече пълен изход: спира всяка следваща обработка, за да не гони навигацията.
let loggingOut = false;

export const isLoggingOut = () => loggingOut;

/** Пази от примка: аварийната навигация не бива да се повтаря безкрай. */
const RECOVERY_KEY = "ctx:recov";
const RECOVERY_LIMIT = 4;
const RECOVERY_WINDOW_MS = 30_000;

function tooManyRecoveries(): boolean {
  const now = Date.now();
  const [rawCount, rawFirst] = (ssGet(RECOVERY_KEY) || "").split("|");
  const first = Number(rawFirst) || 0;
  const fresh = now - first > RECOVERY_WINDOW_MS;
  const count = fresh ? 1 : (Number(rawCount) || 0) + 1;
  ssSet(RECOVERY_KEY, `${count}|${fresh ? now : first}`);
  return count > RECOVERY_LIMIT;
}

/** Пълна навигация към „/": новият документ стъпва на АКТУАЛНОТО съдържание
 * на localStorage (ПИН екран без служител; новият служител при осиновяване;
 * новият токен при личен акаунт). Пълна, за да не остане стар Redux/кеш/сокет. */
function reloadHome() {
  window.location.href = "/";
}

/**
 * Проверява ЖИВ ЛИ Е токенът на терминала.
 *
 * СУРОВ axios, НЕ инстанцията `api`: request интерсепторът в api/client.ts
 * презаписва Authorization БЕЗУСЛОВНО и би проверил грешния токен, а отговорът
 * би влязъл пак в response интерсептора (рекурсия).
 *
 * GET /user/info е нарочен избор — няма изискване за право, не гледа архивиран
 * ресторант и не мутира нищо (SessionActivityMiddleware мачва само по
 * session_token, така че пробата с X при влязъл служител не пипа базата).
 * Отговорът НИКОГА не влиза в Redux — това са ролите на ТЕРМИНАЛА, не на човека.
 */
export async function probeTerminalToken(): Promise<"alive" | "dead" | "unknown"> {
  const x = getTerminalToken();
  // Липсващ X е „мъртъв" БЕЗ заявка: без Authorization хедър FastAPI връща 403
  // „Not authenticated", което тук би било разчетено погрешно.
  if (!x) return "dead";

  try {
    await axios.get(`${API_BASE_URL}/user/info`, {
      headers: { Authorization: `Bearer ${x}` },
      timeout: 5000,
    });
    return "alive";
  } catch (error) {
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;
    // 401 = сесията е угасена или токенът е невалиден; 403 = акаунтът на касата
    // е деактивиран или лицензът е спрян; 400 = токен без restaurant_id. И трите
    // значат неизползваем терминал. ВСИЧКО останало (404 при изоставаща org
    // реплика, 5xx, липсващ отговор при мрежов проблем) НЕ доказва нищо → не
    // изхвърляме никого.
    // Спрян лиценз: пълният изход след „dead" трябва да каже ЗАЩО на /login.
    const detail: unknown = axios.isAxiosError(error)
      ? error.response?.data?.detail
      : undefined;
    if (status === 403 && isLicenseBlockedDetail(detail)) {
      rememberBlockedLicense(detail);
    }
    if (status === 400 || status === 401 || status === 403) return "dead";
    return "unknown";
  }
}

/** Пълен изход от устройството — иска потребител и парола, за да се върне. */
export function fullLogout() {
  loggingOut = true;
  clearToken();
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

/**
 * Лицензът на ресторанта е спрян: след гратисния период BE отказва ВСЯКА
 * заявка с 403 `license_expired` (станционният WS — с кадър със същото тяло
 * преди 1008). Мъртва е и сесията на ТЕРМИНАЛА, затова изходът е пълен — към
 * /login, не до ПИН — на личен акаунт и на общ терминал еднакво, а /login
 * показва модала „Достъпът е спрян" (`LicenseNoticeHost`).
 *
 * Еднократно: десетки заявки гърмят наведнъж, изходът е един (`loggingOut`).
 * Отказът се оставя за /login дори когато друг провал вече е започнал изхода.
 *
 * @param failedToken токенът на отказаната заявка/сокет. На общ терминал, ако
 *   не е токенът на самия терминал, отказан е служителят — терминалът може да е
 *   жив в режим „само преглед" (`lib/licenseReadOnly.ts`): проба, и при жив
 *   или неизвестен X — обратно до ПИН с модала, не пълен изход.
 */
export function endSessionForExpiredLicense(
  detail: LicenseBlockedDetail,
  failedToken?: string | null,
) {
  if (window.location.pathname === "/login") {
    // Навигация няма — документът остава и модалът се показва веднага.
    clearToken();
    announceBlockedLicense(detail);
    return;
  }
  rememberBlockedLicense(detail);
  if (loggingOut) return;
  const terminalToken = getTerminalToken();
  if (
    getAccountIsPersonal() === false &&
    failedToken &&
    terminalToken &&
    failedToken !== terminalToken
  ) {
    returnEmployeeToPin(failedToken);
    return;
  }
  fullLogout();
}

/** Служителят на общ терминал е спрян от лиценза: при доказано мъртъв X —
 * пълен изход, иначе записът му си отива и документът се зарежда наново на
 * ПИН екрана; отказът (вече в sessionStorage) показва модала там. */
function returnEmployeeToPin(failedToken: string) {
  if (pending) return;
  pending = (async () => {
    try {
      if ((await probeTerminalToken()) === "dead") {
        fullLogout();
        return;
      }
      clearEmployeeRecordIf(failedToken);
      clearCachedUserInfo(failedToken);
      if (tooManyRecoveries()) fullLogout();
      else reloadHome();
    } finally {
      pending = null;
    }
  })();
}

type FailureSource = "http-401" | "ws-1008" | "ws-no-token" | "restore";

/**
 * Единната обработка на „токенът не върши работа".
 *
 * @param failedToken токенът, с който е тръгнала провалилата се заявка.
 *   ЗАДЪЛЖИТЕЛЕН за compare-and-clear: сравняването с текущия Redux токен е
 *   грешно — докато 401-цата се върне, Redux вече може да държи ЖИВИЯ токен на
 *   новия служител и безусловното триене би убило точно него.
 */
export function handleAuthFailure(opts: {
  failedToken?: string | null;
  source: FailureSource;
}): Promise<void> {
  if (loggingOut) return Promise.resolve();
  if (pending) return pending;

  // На /login гейтът не е монтиран и няма какво да се възстановява — екранът
  // за вход сам си обработва грешките.
  if (window.location.pathname === "/login") return Promise.resolve();

  // Личен акаунт (вкл. мониторните/шпикер акаунтите): няма ПИН слой, няма
  // междинно състояние — пълен изход, без нито една заявка. ЕДНО изключение:
  // ако съсeден таб междувременно е влязъл наново, localStorage държи ПО-НОВ
  // токен от провалилия се — тогава го приемаме с презареждане, вместо
  // fullLogout да изтрие тъкмо направения свеж вход.
  if (getAccountIsPersonal() !== false) {
    const current = getTerminalToken();
    if (opts.failedToken && current && current !== opts.failedToken) {
      if (tooManyRecoveries()) fullLogout();
      else reloadHome();
    } else {
      fullLogout();
    }
    return Promise.resolve();
  }

  // Общ терминал без токен на терминала — няма какво да се проверява и няма с
  // какво да се влезе с код.
  if (!getTerminalToken()) {
    fullLogout();
    return Promise.resolve();
  }

  pending = (async () => {
    try {
      // Друг контекст вече е записал ПО-НОВ служител (изход + нов код на
      // съседния прозорец). Провалът е обяснен без нито една заявка: просто се
      // пренареждаме по новото състояние.
      const rec = readEmployeeRecord();
      if (opts.failedToken && rec && rec.t !== opts.failedToken) {
        if (tooManyRecoveries()) fullLogout();
        else reloadHome();
        return;
      }

      const result = await probeTerminalToken();
      if (result === "dead") {
        fullLogout();
        return;
      }

      if (opts.source === "restore") {
        // Гейтът ни чака с await от собствения си catch и сам ще покаже ПИН
        // екрана — навигация не трябва. Записът НЕ се трие: провалът на
        // възстановяването може да е мрежов блип (thunk-ът не различава 401 от
        // прекъсната мрежа), а триенето на ОБЩИЯ запис би свалило през storage
        // събитието и напълно здрав съседен прозорец. Следващият код го
        // презаписва; повторен refresh опитва възстановяването наново.
        return;
      }

      // http-401 / ws-1008: Y е ДОКАЗАНО мъртъв (401-цата/1008 дойде от
      // сървъра) — записът за служителя си отива и оставаме на ПИН. Кешът на
      // самоличността му (lib/userInfoCache.ts) — също, дори записът вече
      // да държи по-нов служител: мъртъв токен няма какво да възкресява.
      clearEmployeeRecordIf(opts.failedToken);
      clearCachedUserInfo(opts.failedToken);
      if (tooManyRecoveries()) fullLogout();
      else reloadHome();
    } finally {
      pending = null;
    }
  })();

  return pending;
}

// ─── Ръкостискане „има ли жив съсед" ────────────────────────────────────────
// Нов таб/прозорец не вижда чуждия sessionStorage, затова не може сам да
// различи „студен старт след изключване на касата" от „втори прозорец до вече
// работещ". Пита го през localStorage: записва nonce в `ping`, а всеки жив
// контекст с влязъл служител отговаря в pong. Няма отговор за 250 ms → студен
// старт → ПИН екран (заварено поведение: изключена и пусната наново каса иска
// код). Ползваме storage event, а не BroadcastChannel — listener-ът и без това
// е задължителен за синхронизацията, значи един механизъм вместо два.

/**
 * „Този контекст вече е бил жив?" — различава refresh на работещ прозорец от
 * студен старт. Смята се ВЕДНЪЖ на зареден документ: React.StrictMode монтира
 * ефектите двойно в dev и без този кеш второто монтиране винаги би виждало
 * маркера, който само е записало.
 */
let warmProbe: boolean | null = null;
export function isWarmContext(): boolean {
  if (warmProbe === null) {
    warmProbe = ssGet(CTX_KEY) === "1";
  }
  return warmProbe;
}

/**
 * Маркира контекста като „топъл". Вика се ЕДВА когато прозорецът реално носи
 * жив служител (успешен ПИН вход, успешно възстановяване, осиновяване) — НЕ
 * при монтиране: иначе прозорец, паднал до ПИН при студен старт, се маркираше
 * топъл и първият F5 възкресяваше снощния служител без код.
 */
export function markContextWarm() {
  warmProbe = true;
  ssSet(CTX_KEY, "1");
}

export function pingSiblings(timeoutMs = 250): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let done = false;
    const nonce = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

    const finish = (answered: boolean) => {
      if (done) return;
      done = true;
      window.removeEventListener("storage", onStorage);
      window.clearTimeout(timer);
      try {
        localStorage.removeItem(PING_KEY);
        localStorage.removeItem(PONG_KEY);
      } catch {
        /* storage недостъпен */
      }
      resolve(answered);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === PONG_KEY && e.newValue === nonce) finish(true);
    };

    const timer = window.setTimeout(() => finish(false), timeoutMs);
    window.addEventListener("storage", onStorage);

    try {
      localStorage.setItem(PING_KEY, `${nonce}|${Date.now()}`);
    } catch {
      // Без storage няма как да питаме — държим се като студен старт.
      finish(false);
    }
  });
}

/** Отговор на ping от жив контекст с влязъл служител. */
export function answerPing(rawPing: string) {
  const nonce = rawPing.split("|")[0];
  if (!nonce) return;
  try {
    localStorage.setItem(PONG_KEY, nonce);
  } catch {
    /* storage недостъпен */
  }
}
